import { html, LitElement, unsafeCSS, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './form-x';
import './landing-x';
import './session-x';
import './success-form';
import './context-form';
import './accordion-form';
import './container-form';

import '../e-list';
import '../snackbar-component';
import { Logger } from '../../../core/logger';
import { GetSection } from '../../../core/parsers/commands/get-section';
import { callServiceParams } from '../../../core/mixins/http-mixin-2';
import { sectionSelector } from '../../../core/mixins/adaptiveui-mixin';
import tailwindStyles from "../../../index.css?inline";

interface FeedDetail {
  title: string;
  details?: {
    render?: {
      mode: string | number;
    };
    wss_params?: { [key: string]: any };
  };
  [key: string]: any;
}

@customElement('form-render')
export class FormRender extends LitElement {

  static styles = [unsafeCSS(tailwindStyles),
    css `
    /* Loading overlay */
      .overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(var(--md-sys-color-primary-rgb), 0.5);
        backdrop-filter: blur(4px);        z-index: 50;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `]

  @property({ type: Array }) sections: any[] = [];
  @property({ type: Number }) pos = 0;
  @property({ type: String }) rendermode = '';
  @property({ type: String }) parent = '';

  @property({ type: Boolean }) isSectionPage = false;
  @property({ type: Boolean }) loading = false;
  @property({ type: Object }) params: { [key: string]: any } | null = null;
  @property({ type: String }) template = '';

  // Add stepParams property
  @property({ type: Object }) stepParams: { [key: string]: any } = {};

  // Track max position reached for stepper
  @state() private maxPosReached = 0;
  @state() private activeAccordionId: string | null = null;
  @state() private processingMessage = false;
  @state() private processedMessageIds = new Set<string>();
  @state() private activeFeed: FeedDetail | null = null;

  // Snackbar state
  @state() private snackbarMessage = '';
  @state() private snackbarType: 'success' | 'error' | 'warning' | 'info' = 'info';
  @state() private snackbarVisible = false;
  @state() private snackbarTimeout: any = null;

  @state() private hasSubmitted = false;
  @state() private feeds: FeedDetail[] = [];
  @state() private currentFeedIndex = 0;
  @state() private newFeed: FeedDetail | null = null;
  @state() wsConnected = false;
  private ws?: WebSocket;
  private wsRetry = 0;
  private wsReconnectTimer?: number;
  private subscribedChannels = new Set<string>();

  private _feed: FeedDetail = { title: '' };
  currentWssUrl: string | null = null;

  @property({ type: Object })
  set feed(val: FeedDetail) {
    const oldVal = this._feed;
    if (val === oldVal) {
      return;
    }
    this._feed = val;
    this.sections = [{
      oe: (this._feed as any).oe || [],
      element: (this._feed as any).element || '',
      _sectionJson: (this._feed as any)._sectionJson || { input_var: [] },
      title: this._feed.title || this._feed._title,
      _details: this._feed._details || {}
    }] as any[];

    this.pos = 0;
    this.maxPosReached = 0;
    this.hasSubmitted = false;
    this.currentFeedIndex = 0;
    this.loading = false;

    this.feeds = [{
      ...this._feed,
      service: (this._feed as any)._sectionJson?.input_var?.[11] || '',
      autoSubmits: this._feed.autoSubmits || false
    }];

    if (
      this._feed.details &&
      (this._feed.details as any).render &&
      (this._feed.details as any).render.mode != null
    ) {
      this.rendermode = String((this._feed.details as any).render.mode);
    }
    this.requestUpdate("feed", oldVal);
  }

  get feed(): FeedDetail {
    return this._feed;
  }

  static get is() {
    return "form-render";
  }

  constructor() {
    super();
    this.stepParams = {};
    this.handleMessage = this.handleMessage.bind(this);
    this.handleBeforeUnload = this.handleBeforeUnload.bind(this);
    this.handlePageHide = this.handlePageHide.bind(this);
    this.cleanupMergedParams = this.cleanupMergedParams.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.openPaymentSocket();

    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("pagehide", this.handlePageHide);
    document.addEventListener("visibilitychange", this.handleVisibilityChange.bind(this));

    this.addEventListener('step-navigate-back', ((e: CustomEvent) => {
      const newPos = e.detail.newPos;
      if (newPos < this.pos) {
        this.sections = this.sections.slice(0, newPos + 1);
        this.maxPosReached = newPos;
      }
    }) as EventListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.wsReconnectTimer) clearTimeout(this.wsReconnectTimer);
    this.ws?.close(1000, 'component unmounted');

    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("pagehide", this.handlePageHide);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange.bind(this));

    if (this.snackbarTimeout) {
      clearTimeout(this.snackbarTimeout);
    }

    this.cleanupMergedParams();

    this.processingMessage = false;
    this.processedMessageIds.clear();
  }

  // ---------------------------------------------------------------------------
  // Payment WSS
  // ---------------------------------------------------------------------------

  private openPaymentSocket() {
    const af = this.__computeActiveFeed();

    let details: any = {};
    try {
      details = JSON.parse(JSON.stringify(af?._sectionJson?.input_var?.[14] || {}));
    } catch {
      return null;
    }

    const url =
      details?.payment_wss_url ||
      details?.ws_url ||
      details?.websocket_url ||
      details?.wss ||
      null;

    if (typeof url !== 'string') return null;
    if (!/^wss?:\/\//i.test(url)) return null;

    this.currentWssUrl = url;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.wsConnected = true;
      this.wsRetry = 0;
      console.log('[form-render] payment WSS connected ', this.currentWssUrl,this._feed);

      // Re-subscribe to any channels we care about (e.g. after reconnect)
      for (const ch of this.subscribedChannels) {
        this.ws!.send(JSON.stringify({ type: 'subscribe', channel: ch, ...(this.activeFeed?.wss_params ?? {}) }));
      }
      // Global channel used by payment-status pushes
      this.ws!.send(JSON.stringify({ type: 'subscribe', channel: 'payment.status', ...(this.activeFeed?.wss_params ?? {}) }));
    };

    this.ws.onmessage = (ev) => {
      let payload: any;
      try { payload = JSON.parse(ev.data); } catch { return; }
      this.onPaymentPush(payload);
    };

    this.ws.onerror = (e) => console.error('[form-render] WSS error', e);

    this.ws.onclose = (ev) => {
      this.wsConnected = false;
      if (ev.code === 1000) return; // intentional close
      const delay = Math.min(30_000, 1000 * 2 ** this.wsRetry++);
      console.warn(`[form-render] WSS closed (${ev.code}); retrying in ${delay}ms`);
      this.wsReconnectTimer = window.setTimeout(() => this.openPaymentSocket(), delay);
    };
  }

  /**
   * Push shapes handled here:
   *
   *   { "event": "listening",    "purchase_order_id": 151, "reference": "MIPA-TUY93", "status": "UNPAID" }
   *   { "event": "order_status", "purchase_order_id": 151, "reference": "MIPA-TUY93", "status": "PAID"   }
   *
   * Older feeds may wrap the body under `message` or use `type` instead of `event`.
   * All three are tolerated.
   */
private onPaymentPush(raw: any) {
  if (!raw) return;

  const payload = raw.message ?? raw;
  const eventName = (payload.event || payload.type || '').toString().toLowerCase();

  // Subscription ack → payment-status shows its own loader, nothing to do here.
  if (eventName === 'listening') {
    console.log('[form-render] WSS listening', payload);
    return;
  }

  // Only react to an order_status (or payment.status) push with terminal PAID.
  if (eventName !== 'order_status' && eventName !== 'payment.status') return;

  const status = (payload.status ?? '').toString();
  if (status !== 'PAID') return;

  const purchaseOrderId = payload.purchase_order_id != null
    ? String(payload.purchase_order_id)
    : '';
  const reference = (payload.reference ?? '').toString();
  const service =
    payload.service ||
    this.__computeActiveFeed()?._sectionJson?.input_var?.[11];

  if (!service) {
    console.warn('[form-render] PAID push without a service — ignoring', payload);
    return;
  }

  // Dedupe: the same order must only autosubmit once.
  const id = `${service}_${reference}_${purchaseOrderId}`;
  if (this.processedMessageIds.has(id)) return;
  if (this.processingMessage || this.hasSubmitted || this.loading) {
    console.log('[form-render] PAID push ignored — submit already in progress', id);
    return;
  }

  this.processedMessageIds.add(id);

  // Optional: cap the dedupe set so it can't grow forever.
  if (this.processedMessageIds.size > 100) {
    const iterator = this.processedMessageIds.values();
    for (let i = 0; i < 50; i++) {
      const next = iterator.next();
      if (next.done || next.value === undefined) break;
      this.processedMessageIds.delete(next.value);
    }
  }

  // Merge the payment payload into stepParams BEFORE autosubmit so the
  // serialized form data and the pushed values are both available to the
  // service call.
  this.stepParams = this.mergeParams(
    {
      ...payload,
      purchase_order_id: purchaseOrderId,
      reference,
      status,
    },
    this.stepParams
  );
  (window as any).mergedParams = this.stepParams;

  console.log('[form-render] PAID push → autosubmitting form', {
    purchase_order_id: purchaseOrderId,
    reference,
    status,
    service,
    stepParams: this.stepParams,
  });

  // Flag so submitForm() doesn't get blocked by its own `loading` guard if
  // we somehow re-enter, and so we don't double-fire from handleMessage.
  this.hasSubmitted = true;
  this.processingMessage = true;

  // Autosubmit via the normal flow (validate → serialize → merge → call).
  void this._autosubmitFromPush();
}

private async _autosubmitFromPush() {
  try {
    // submitForm() does its own loading/validation/serialize/merge/call.
    await this.submitForm();
  } catch (err: any) {
    console.error('[form-render] autosubmit failed', err);
    this.showError(err?.message ?? 'Payment processing failed');
    this.hasSubmitted = false;
  } finally {
    setTimeout(() => {
      this.processingMessage = false;
    }, 1000);
  }
}

  private getActiveWssUrl(): string | null {
    const af = this.__computeActiveFeed();
    let details: any = {};

    try {
      details = JSON.parse(JSON.stringify(af?._sectionJson?.input_var?.[14] || {}));
    } catch {
      return null;
    }

    const url =
      details?.payment_wss_url ||
      details?.wss_url ||
      details?.ws_url ||
      details?.websocket_url ||
      details?.wss ||
      null;

    if (typeof url !== 'string') return null;
    if (!/^wss?:\/\//i.test(url)) return null;

    return url;
  }

  private closeSocket(code = 1000, reason = 'closing') {
    if (this.wsReconnectTimer) {
      clearTimeout(this.wsReconnectTimer);
      this.wsReconnectTimer = undefined;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.onopen = null;
      try { this.ws.close(code, reason); } catch { /* ignore */ }
      this.ws = undefined;
    }
    this.wsConnected = false;
  }

  getActivePaymentChannel(): string | null {
    const af = this.__computeActiveFeed();
    try {
      const details = JSON.parse(JSON.stringify(af?._sectionJson?.input_var?.[14] || {}));
      return details?.wss_channel || details?.channel || null;
    } catch {
      return null;
    }
  }

  private syncWssWithActiveFeed() {
    const targetUrl = this.getActiveWssUrl();

    if (!targetUrl) {
      if (this.ws) this.closeSocket(1000, 'no wss for step');
      this.currentWssUrl = null;
      return;
    }

    if (this.currentWssUrl !== targetUrl || !this.ws) {
      this.openPaymentSocket();
    }
  }

  private cleanupMergedParams() {
    if (window.hasOwnProperty('mergedParams')) {
      try {
        delete (window as any).mergedParams;
        console.log('window.mergedParams cleaned up');
      } catch (e) {
        (window as any).mergedParams = undefined;
      }
    }
  }

  private handleBeforeUnload(_event: BeforeUnloadEvent) {
    this.cleanupMergedParams();
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has('newFeed') && this.newFeed) {
      this.handleNewFeed();
    }

    if (
      changedProperties.has('pos') ||
      changedProperties.has('sections') ||
      changedProperties.has('feed')
    ) {
      const af = this.__computeActiveFeed();

      if (af._sectionJson?.input_var?.[14]) {
        const activeFeedJson = JSON.parse(JSON.stringify(af._sectionJson.input_var[14]));
        this.activeFeed = activeFeedJson;
        console.log("[form-render] Active feed updated, checking for WSS sync:", this.activeFeed);
        const payment_wss_url = activeFeedJson?.payment_wss_url || null;
        const payment_wss_check = activeFeedJson?.payment_wss_check || null;

        if (payment_wss_url && payment_wss_check) {
          this.syncWssWithActiveFeed();
        }
      }
    }
  }

  private handlePageHide(_event: PageTransitionEvent) {
    this.cleanupMergedParams();
  }

  private handleVisibilityChange() {
    if (document.hidden) {
      // no-op for now
    }
  }

  private setActiveAccordion(id: string | null) {
    this.activeAccordionId = id;
    this.requestUpdate();
  }

  private handleNewFeed() {
    if (!this.newFeed) return;

    const feed = this.feeds[this.currentFeedIndex] || this.newFeed;

    if (feed.autoSubmits === true) {
      this.submitForm();
    } else {
      this.feeds = [...this.feeds, this.newFeed];
      this.currentFeedIndex = this.feeds.length - 1;
    }

    this.newFeed = null;
  }

  async handleMessage(e: MessageEvent) {
    const data = e?.data;
    if (!data?.message) return;

    const { message } = data;
    const { status,  timestamp } = message;
    const af = this.__computeActiveFeed();
    const service = af._service;
    console.log("af in handlemessage", af,service);
    const messageId = `${service}_${timestamp || Date.now()}`;

    if (this.processedMessageIds.has(messageId)) {
      console.log("Duplicate message detected, ignoring:", messageId);
      return;
    }
    if (this.processingMessage) {
      console.log("Already processing a message, ignoring duplicate");
      return;
    }

    if (status === 'PAID' && service && !this.hasSubmitted && !this.loading) {
      this.processingMessage = true;
      this.hasSubmitted = true;
      this.loading = true;

      this.processedMessageIds.add(messageId);

      if (this.processedMessageIds.size > 100) {
        const iterator = this.processedMessageIds.values();
        for (let i = 0; i < 50; i++) {
          const nextItem = iterator.next();
          if (nextItem.done || nextItem.value === undefined) break;
          this.processedMessageIds.delete(nextItem.value);
        }
      }

      try {
        const mergedParams = this.mergeParams(this.params, message);
        console.log("Calling service with merged params:", mergedParams);
        (window as any).mergedParams = mergedParams;
        const response = await callServiceParams(service, mergedParams);
        this.handleResponse(response.response);
      } catch (error) {
        console.error("Error processing message:", error);
        this.showError("An error occurred while processing your request.");
        this.hasSubmitted = false;
        this.processingMessage = false;
      } finally {
        this.loading = false;
        setTimeout(() => {
          this.processingMessage = false;
        }, 1000);
      }
    } else if (this.hasSubmitted) {
      console.log("Form already submitted, ignoring duplicate message");
    } else if (this.loading) {
      console.log("Already loading, ignoring duplicate message");
    } else if (this.processingMessage) {
      console.log("Already processing a message, ignoring duplicate");
    }
  }

  private showSnackbar(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') {
    if (this.snackbarTimeout) {
      clearTimeout(this.snackbarTimeout);
      this.snackbarTimeout = null;
    }

    this.snackbarMessage = message;
    this.snackbarType = type;
    this.snackbarVisible = true;

    const duration = type === 'success' ? 3000 : 5000;
    this.snackbarTimeout = setTimeout(() => {
      this.snackbarVisible = false;
      this.snackbarTimeout = null;
    }, duration);

    this.requestUpdate();
  }

  private hideSnackbar() {
    this.snackbarVisible = false;
    if (this.snackbarTimeout) {
      clearTimeout(this.snackbarTimeout);
      this.snackbarTimeout = null;
    }
  }

  private showSuccess(message: string) {
    this.showSnackbar(message, 'success');
  }

  private showError(message: string) {
    this.showSnackbar(message, 'error');
  }

  private getSnackbarStyles() {
    const baseStyles = 'fixed bottom-4 left-4 z-50 max-w-md w-full md:max-w-sm transform transition-all duration-300 ease-in-out';
    const visibilityStyles = this.snackbarVisible
      ? 'translate-y-0 opacity-100'
      : 'translate-y-full opacity-0 pointer-events-none';

    const typeStyles = {
      success: 'bg-green-50 border-l-4 border-green-500 text-green-700',
      error: 'bg-red-50 border-l-4 border-red-500 text-red-700',
      warning: 'bg-yellow-50 border-l-4 border-yellow-500 text-yellow-700',
      info: 'bg-blue-50 border-l-4 border-blue-500 text-blue-700'
    }[this.snackbarType];

    return `${baseStyles} ${visibilityStyles} ${typeStyles}`;
  }

  private getSnackbarIcon() {
    const icons = {
      success: html`
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
        </div>
      `,
      error: html`
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
      `,
      warning: html`
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </div>
      `,
      info: html`
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        </div>
      `
    };
    return icons[this.snackbarType];
  }

  private renderSnackbar() {
    if (!this.snackbarMessage) return html``;

    return html`
      <div class="${this.getSnackbarStyles()} shadow-lg rounded-lg">
        <div class="p-4">
          <div class="flex items-start">
            ${this.getSnackbarIcon()}
            <div class="ml-3 flex-1">
              <p class="text-sm font-medium">${this.snackbarMessage}</p>
            </div>
            <div class="ml-4 flex-shrink-0">
              <button
                @click="${this.hideSnackbar}"
                class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500 transition ease-in-out duration-150"
              >
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderLoader() {
    if (!this.loading) return html``;

    return html`
      <div class="overlay">
        <div class="text-center">
          <div
            class="w-16 h-16 border-4 border-[var(--md-sys-color-onPrimary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"
          ></div>
          <p class="text-[var(--md-sys-color-onPrimary)]">Loading...</p>
        </div>
      </div>
    `;
  }

  mergeParams(obj1: any, obj2: { [key: string]: any }): { [key: string]: any } {
    const obj3: { [key: string]: any } = {};

    if (obj2) {
      for (const attrname in obj2) {
        if (obj2.hasOwnProperty(attrname)) {
          obj3[attrname] = obj2[attrname];
        }
      }
    }

    if (obj1) {
      for (const attrname in obj1) {
        if (obj1.hasOwnProperty(attrname)) {
          obj3[attrname] = obj1[attrname];
        }
      }
    }

    return obj3;
  }

  get form(): any {
    return this.shadowRoot?.querySelector("#form") || null;
  }

  private __computeActiveFeed(): any {
    if (this.sections.length && this.pos >= 0 && this.pos < this.sections.length) {
      return this.sections[this.pos];
    }

    return {
      oe: [],
      element: '',
      _sectionJson: {
        input_var: []
      },
      title: this.sections[this.pos]?.title || '',
      _element: '',
    };
  }

  private activeFeedOe(): any[] {
    const af = this.__computeActiveFeed();
    return af.oe || af._oe || [];
  }

  private _onPosChange(evt: CustomEvent<{ pos: number }>) {
    const newPos = evt.detail.pos;

    if (newPos < this.pos) {
      this.sections = this.sections.slice(0, newPos + 1);
      this.maxPosReached = newPos;
      (window as any).mergedParams = this.stepParams;
      console.log(`Moving back to step ${newPos}, preserving stepParams:`, this.stepParams);
    } else if (newPos > this.maxPosReached) {
      this.maxPosReached = newPos;
    }

    this.pos = newPos;
  }

  eSubmitForm(): any {
    console.log('eSubmitForm called');
  }

  handleResponse(response: any) {
    const responseCode = response.response_status || response.code || '00';

    if (responseCode === '00') {
      this.showSuccess(response.last_response || 'Operation completed successfully!');
    } else {
      const errorMessage = response.last_response || response.response?.message || 'An error occurred';
      this.showError(`Error: ${errorMessage}`);
    }

    // New Step Section Response
    if (response.response && response.response[`get_section`]) {
      const sectionPayload = response;
      const section = sectionSelector(sectionPayload);

      const _computeFeed = (getSection: { _pageGroups: any[]; }) => {
        var pageGroup = getSection._pageGroups[0];
        return pageGroup;
      };

      const active_feed = _computeFeed(section);
      const pageSections = active_feed._tabs;

      const newFeed = pageSections[0]._feedList[0];

      const enhancedFeed = {
        ...newFeed,
        title: newFeed.title || active_feed.title
      };

      if (this.sections.length > this.pos + 1) {
        this.sections[this.pos + 1] = enhancedFeed;
      } else {
        this.sections.push(enhancedFeed);
      }

      this.maxPosReached = Math.max(this.maxPosReached, this.pos + 1);

      this.pos += 1;

      this.requestUpdate();
    }
  }

  closePopUpForm() {
    const formRender = document.querySelector("form-render");
    if (!formRender?.shadowRoot) {
      console.warn("form-render element or shadowRoot not found");
      return;
    }
    const popUpContainer = formRender.shadowRoot.querySelector("popup-x");
    if (popUpContainer) {
      //@ts-ignore
      popUpContainer.style.display = "none";
    }
  }

  async submitForm() {
    try {
      if (this.loading) {
        Logger.i.warn("A Submit is already in processing");
        return;
      }
      this.loading = true;

      const formValid = this.form?.validate();
      if (!formValid) {
        console.warn("invalid FORM ", this.shadowRoot?.querySelector("container-form"), this.form, formValid);
        this.showError('Please fix the validation errors before submitting.');
        this.loading = false;
        return;
      }

      const formData = await this.form?.serialize();
      console.log("form data ", formData);

      this.stepParams = this.mergeParams(formData, this.stepParams);

      const af = this.__computeActiveFeed();
      const service = af._sectionJson.input_var[11];
      (window as any).mergedParams = this.stepParams;
      console.log("this.stepParams ", this.stepParams);

      const response = await callServiceParams(service, this.stepParams);
      this.loading = false;

      this.handleResponse(response.response);

      return response;
    } catch (error: any) {
      console.log('Submission error:', error);
      this.loading = false;
      this.showError(`Submission failed: ${error.message || 'Unknown error'}`);
    }
  }

  submitForm2(service: any): any {
    console.log('submitForm2 called with service:', service);
  }

  async skipForm(service: any, params: Object): Promise<any> {
    try {
      const skipFormService = service;
      if (this.loading) {
        Logger.i.warn("A Submit is already in processing");
        return;
      }
      this.loading = true;

      const skipFormParams = {
        ...this.stepParams,
        ...((window as any).mergedParams || {})
      };

      console.log("skipFormParams ", skipFormParams);

      const finalParams = this.mergeParams(params, skipFormParams);

      (window as any).mergedParams = finalParams;

      console.log("skipFormParams ", skipFormParams);

      const response = await callServiceParams(skipFormService, finalParams);
      this.loading = false;

      this.handleResponse(response.response);

      return response;

    } catch (error: any) {
      console.log('Submission error:', error);
      this.loading = false;
      this.showError(`Submission failed: ${error.message || 'Unknown error'}`);
    }
  }

  closeWindow(service: any): any {
    console.log('closeWindow called with service:', service);
  }

  paramsCopy(): any {
    console.log('paramsCopy called');
    return {};
  }

  _dialog(...args: any[]): void {
    console.log(" === args are ", args);
    const adaptiveUi = document.querySelector("my-element");

    if (args[0] instanceof GetSection) {
      if (!args[1]) {
        Logger.i.warn(`[DEPRECATED API USAGE]
          Whatever called this should update to include the get_section service and params`);
      }
    } else {
      args[1] = this.mergeParams(args[1], this.stepParams);
    }

    if (adaptiveUi && (adaptiveUi as any).dialogView) {
      (adaptiveUi as any).dialogView.apply(adaptiveUi, args);
      console.log("dialogView ", (adaptiveUi as any).dialogView);
    } else {
      console.warn('Adaptive UI element or dialogView method not found');
    }
  }

  updateStepParams(newParams: { [key: string]: any }): void {
    this.stepParams = { ...this.stepParams, ...newParams };
    this.requestUpdate();
  }

  clearStepParams(): void {
    this.stepParams = {};
    this.requestUpdate();
  }

  render() {
    const af = this.__computeActiveFeed();
    const formType = af.element || af._element;
    let form = html``;

    let formDetailsString = '';
    let formDetailsObject: any = {};

    try {
      formDetailsString = JSON.stringify(af._sectionJson.input_var[14] || {});
      formDetailsObject = JSON.parse(formDetailsString);
    } catch (error) {
      console.warn('Error parsing form details:', error);
      formDetailsObject = {};
    }

    if (formType === "FORM") {
      const formHeaderColor = formDetailsObject["background_color"] || '#ffffff';
      const logo = formDetailsObject["logo"] || '';
      const height = formDetailsObject["height"] || '';
      const maxHeight = formDetailsObject["maxHeight"] || '';
      const formClasses = formDetailsObject["classes"] || '';

      const inputVar = af?._sectionJson?.input_var || [];
      const sectionJson = af?._sectionJson || {};

      form = html`
        <form-x
          class=${`form-card ${this.parent} ${window.TEMPLATE} ${formClasses}`.trim()}
          id="form"
          .isSectionPage=${this.isSectionPage}
          loading="${this.loading}"
          .sections=${this.sections}
          .headerBgColor=${formHeaderColor}
          .pos=${this.pos}
          .maxPosReached=${this.maxPosReached}
          @pos-change=${this._onPosChange}
          .logo=${logo}
          .height=${height}
          .maxHeight=${maxHeight}
          .details=${formDetailsObject}
          .oe=${this.activeFeedOe()}
          .sectionJson=${sectionJson}
          .inputVar=${inputVar}
        >
          <e-list
            slot="body"
            .renderMode=${this.rendermode}
            .fr=${this}
            .oe=${this.activeFeedOe()}
            .formType=${"FORM"}
            .title=${af?.title || 'Form'}
            .inputVar=${inputVar}
            .sectionJson=${sectionJson}
          ></e-list>
        </form-x>
      `;
    }
    else if (formType === "SESSION FORM") {
      form = html`
        <style>
          .session__title{
            margin-bottom: 22px;
            font-size: 25px;
            font-weight: lighter;
            text-align: center;
          }
        </style>
        <session-x
          id="form"
          .isSectionPage=${this.isSectionPage}
          .af=${af}
          .feed=${this.feed}
          title="${this.feed.title}"
          loading="${this.loading}"
          .sections=${this.sections}
          .pos=${this.pos}
          .maxPosReached=${this.maxPosReached}
          @pos-change=${this._onPosChange}
          .details=${formDetailsObject}
          class="flex flex-col items-center justify-center"
        >
          <e-list
            slot="body"
            .fr=${this}
            .oe=${this.activeFeedOe()}
            class="w-full flex justify-center"
          ></e-list>
        </session-x>
      `;
    }
    else if (formType === "LANDING FORM") {
      form = html`
       <landing-x class="h-full block"  id="form" .isSectionPage=${this.isSectionPage}>
        <e-list slot="body" .fr=${this} .oe=${this.activeFeedOe()} ></e-list>
      </landing-x>
      `;
    }
    else if (formType === "PAYMENTS FORM") {
      form = html`
       <context-form  id="context-form form" .af=${af} .isSectionPage=${this.isSectionPage}>
        <h5>Payments Form</h5>
        <p>This is a placeholder for Payments Form.</p>
        <e-list slot="body" .fr=${this} .oe=${this.activeFeedOe()} ></e-list>
      </context-form>
      `;
    }
    else if (formType == "CONTAINER FORM") {
      const formHeaderColor = formDetailsObject["background_color"] || '#ffffff';
      const logo = formDetailsObject["logo"] || '';
      const height = formDetailsObject["height"] || '';
      const maxHeight = formDetailsObject["maxHeight"] || '';
      const formClasses = formDetailsObject["classes"] || '';

      const inputVar = af?._sectionJson?.input_var || [];
      const sectionJson = af?._sectionJson || {};

      form = html`
        <container-form
          class=${`${this.parent} ${window.TEMPLATE} ${formClasses}`.trim()}
          id="form"
          .isSectionPage=${this.isSectionPage}
          loading="${this.loading}"
          .sections=${this.sections}
          .headerBgColor=${formHeaderColor}
          .pos=${this.pos}
          .maxPosReached=${this.maxPosReached}
          @pos-change=${this._onPosChange}
          .logo=${logo}
          .height=${height}
          .maxHeight=${maxHeight}
          .details=${formDetailsObject}
          .oe=${this.activeFeedOe()}
          .sectionJson=${sectionJson}
          .inputVar=${inputVar}
        >
          <e-list
            slot="body"
            .renderMode=${this.rendermode}
            .fr=${this}
            .pos=${this.pos}
            .oe=${this.activeFeedOe()}
            .formType=${"CONTAINER FORM"}
            .title=${af?.title || 'Form'}
            .inputVar=${inputVar}
            .sectionJson=${sectionJson}
          ></e-list>
        </container-form>
      `;
    }
    else if (formType === "ACCORDION FORM") {
      form = html`
        <div class="accordion-form-container">
          <accordion-x
            id="form"
            .isSectionPage=${this.isSectionPage}
            .af=${af}
            .feed=${this.feed}
            .title=${af.title || 'Accordion Form'}
            loading="${this.loading}"
            .sections=${this.sections}
            .pos=${this.pos}
            .maxPosReached=${this.maxPosReached}
            @pos-change=${this._onPosChange}
            .details=${formDetailsObject}
            .oe=${this.activeFeedOe()}
            .service=${af._sectionJson?.input_var?.[11] || ''}
            .skipForm=${this.skipForm.bind(this)}
            ._dialog=${this._dialog.bind(this)}
            .getForm2=${() => {}}
            .formType=${"ACCORDION FORM"}
            .fr=${this}
            .renderMode=${this.rendermode}
            .activeAccordion=${this.activeAccordionId}
            .setActiveAccordion=${this.setActiveAccordion.bind(this)}
          >
            <e-list
              slot="body"
              .fr=${this}
              .oe=${this.activeFeedOe()}
              class="w-full flex justify-center"
            ></e-list>
          </accordion-x>
        </div>
      `;
    }
    else if (formType === "SUCCESS FORM") {
      const formHeaderColor = formDetailsObject["background_color"] || '#ffffff';
      const logo = formDetailsObject["logo"] || '';
      const height = formDetailsObject["height"] || '';
      const maxHeight = formDetailsObject["maxHeight"] || '';

      form = html`
        <success-form
          class=${`form-card ${this.parent} ${window.TEMPLATE}`}
          id="form"
          .isSectionPage=${this.isSectionPage}
          loading="${this.loading}"
          .sections=${this.sections}
          .headerBgColor=${formHeaderColor}
          .pos=${this.pos}
          .maxPosReached=${this.maxPosReached}
          @pos-change=${this._onPosChange}
          .logo=${logo}
          .height=${height}
          .maxHeight=${maxHeight}
          .details=${formDetailsObject}
          .oe=${this.activeFeedOe()}
          .title=${af.title || 'Form'}
          .subtitle=${formDetailsObject.subtitle || 'Form'}
        >
          <e-list
            slot="body"
            .renderMode=${this.rendermode}
            .fr=${this}
            .oe=${this.activeFeedOe()}
            .formType=${"FORM"}
            .title=${af.title || 'Form'}
          ></e-list>
        </success-form>
      `;
    }

    return html`
      <div class="relative h-full">
        ${form}
        ${this.renderLoader()}
        ${this.renderSnackbar()}
      </div>
    `;
  }
}